import React, { useMemo } from "react";
import styled from "styled-components";
import { Step, ChordProgression, ChordMapping } from "../types";

// Consistent blue color for active steps (matches screen panel aesthetic)
const STEP_ACTIVE_COLOR = "#7BC6F4";

interface SequencerStepsProps {
  pattern: Step[];
  currentStep: number;
  progression: ChordProgression; // kept for API compatibility
  chordMapping: ChordMapping; // kept for API compatibility
  onToggleStep: (stepIndex: number) => void;
  mappingMode?: string | null;
  stepsCount: number;
}

interface StepItemProps {
  step: Step;
  index: number;
  currentStep: number;
  mappingMode: string | null;
  onToggleStep: (stepIndex: number) => void;
}

// Memoized StepItem component - only re-renders when its specific step data changes
const StepItem: React.FC<StepItemProps> = React.memo(
  ({ step, index, currentStep, mappingMode, onToggleStep }) => {
    // In drone mode, use green color for active steps (same as keyboard)
    const DRONE_GREEN = "#23D18E";

    // Determine background color based on step state and mapping mode
    let backgroundColor: string;
    if (!mappingMode) {
      // Not in mapping mode - use consistent blue for chord mode, green for drone mode
      backgroundColor = step.active
        ? step.mode === "drone"
          ? DRONE_GREEN
          : STEP_ACTIVE_COLOR
        : "rgba(255, 255, 255, 0.2)";
    } else {
      // In mapping mode - use mapping-specific colors
      const hasMapping =
        step.fxMixes &&
        step.fxMixes[mappingMode as keyof typeof step.fxMixes] !== undefined;

      if (!step.active) {
        // Inactive steps - no background
        backgroundColor = "transparent";
      } else if (hasMapping) {
        // Active steps with mapping: #F58413
        backgroundColor = "#F58413";
      } else {
        // Active steps without mapping: #714C27
        backgroundColor = "#714C27";
      }
    }

    return (
      <StepContainer
        key={index}
        id={`step-container-${index}`}
        $isActive={step.active}
        $isCurrent={currentStep === index}
      >
        <StepToggle
          id={`step-toggle-${index}`}
          onClick={() => onToggleStep(index)}
        >
          <StepBar
            id={`step-bar-${index}`}
            $isActive={step.active}
            $backgroundColor={backgroundColor}
          />
        </StepToggle>
      </StepContainer>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: return true to skip re-render, false to re-render
    // Skip re-render if all props are the same
    if (
      prevProps.step.active !== nextProps.step.active ||
      prevProps.step.mode !== nextProps.step.mode ||
      prevProps.step.fxMixes !== nextProps.step.fxMixes ||
      prevProps.index !== nextProps.index ||
      prevProps.mappingMode !== nextProps.mappingMode
    ) {
      return false; // Re-render needed
    }

    // Check if currentStep change affects this step
    const wasCurrent = prevProps.currentStep === prevProps.index;
    const isCurrent = nextProps.currentStep === nextProps.index;

    // Only re-render if this step's current status changed
    return wasCurrent === isCurrent; // true = skip, false = re-render
  },
);

StepItem.displayName = "StepItem";

const SequencerSteps: React.FC<SequencerStepsProps> = ({
  pattern,
  currentStep,
  progression: _progression,
  chordMapping: _chordMapping,
  onToggleStep,
  mappingMode = null,
  stepsCount,
}) => {
  // Calculate number of rows (max 16 steps per row)
  const rowsCount = Math.ceil(stepsCount / 16);
  const stepsPerRow = 16;

  // Memoize rows array calculation
  const rows = useMemo(() => {
    const rowsArray: Step[][] = [];
    for (let rowIndex = 0; rowIndex < rowsCount; rowIndex++) {
      const startIndex = rowIndex * stepsPerRow;
      const endIndex = Math.min(startIndex + stepsPerRow, stepsCount);
      rowsArray.push(pattern.slice(startIndex, endIndex));
    }
    return rowsArray;
  }, [pattern, stepsCount, rowsCount]);

  return (
    <SequencerStepsContainer
      id="sequencer-steps-container"
      $rowsCount={rowsCount}
    >
      {rows.map((rowSteps, rowIndex) => {
        const rowStartIndex = rowIndex * stepsPerRow;
        return (
          <StepRow key={rowIndex} $rowsCount={rowsCount}>
            {rowSteps.map((step, stepIndexInRow) => {
              const index = rowStartIndex + stepIndexInRow;

              return (
                <StepItem
                  key={index}
                  step={step}
                  index={index}
                  currentStep={currentStep}
                  mappingMode={mappingMode}
                  onToggleStep={onToggleStep}
                />
              );
            })}
          </StepRow>
        );
      })}
    </SequencerStepsContainer>
  );
};

// Styled Components
const SequencerStepsContainer = styled.div<{ $rowsCount: number }>`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  margin: 0 auto;
  width: 100%;
  padding: 0px 16px 8px 16px;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
`;

const StepRow = styled.div<{ $rowsCount: number }>`
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  flex: 1 1 0;
  min-height: 0;
  height: calc(100% / ${(props) => props.$rowsCount});
  gap: 2px;
`;

const StepContainer = styled.div<{ $isActive: boolean; $isCurrent: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border-radius: 2px;
  border: 2px solid transparent;
  box-sizing: border-box;
  flex: 1 1 0;
  min-width: 0;
  height: 100%;

  ${(props) =>
    props.$isCurrent &&
    `
    border: 2px solid rgba(255, 255, 255, 0.3);
  `}

  ${(props) =>
    props.$isCurrent &&
    props.$isActive &&
    `
    border: 2px solid rgba(255, 255, 255, 0.5);
  `}
`;

const StepToggle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  width: 100%;
  height: 100%;
`;

const StepBar = styled.div<{ $isActive: boolean; $backgroundColor: string }>`
  width: 100%;
  height: 100%;
  border-radius: 3px;
  transition: all 0.2s ease;
  background-color: ${(props) => props.$backgroundColor};

  &:hover {
    opacity: 0.8;
  }

  ${(props) =>
    props.$isActive &&
    `
    box-shadow: 0 0 4px rgba(255, 255, 255, 0.15);
  `}
`;

// Memoize the main component to prevent unnecessary re-renders
export default React.memo(SequencerSteps);
